import { PORTABLE_RECORDING_REF_PATTERN } from "@rekonas/domain";
import { authorizedFetch, parseResponse } from "./api";
import { authDataIdentity } from "./cacheScope";

export interface SummaryReviewRecording {
  recording_id: string; label: string; date: string; duration_seconds: number | null;
  metrics_available: boolean; stages_available: boolean; source_mismatch: boolean;
  quality: string; notices: string[];
}
export interface SummaryEmailPreview {
  recipient: string; can_override_recipient: boolean; preview_revision: string; sendable: boolean;
  recordings: SummaryReviewRecording[];
}
export interface SummaryEmailRequest {
  recording_ids: string[]; client_request_id: string; preview_revision: string;
  recipient?: string;
}
export interface SummaryEmailJob {
  job_id: string; state: "queued" | "preparing" | "sending" | "accepted" | "failed" | "delivery_uncertain";
  code: string; created: string; recording_count: number; status_url: string;
}
const route = "/platform/mobile/v1/recording-summary-emails";
export const summaryEmailKeys = {
  all: () => ["summary-emails", authDataIdentity()] as const,
  history: (cursor: string | null) => [...summaryEmailKeys.all(), "history", cursor] as const,
  job: (id: string) => [...summaryEmailKeys.all(), "job", id] as const,
};
function canonicalIds(ids: string[]) {
  if (!ids.length || ids.length > 10 || ids.some(id => !PORTABLE_RECORDING_REF_PATTERN.test(id))) throw new TypeError("Invalid summary selection");
  return [...new Set(ids)].sort();
}
export function parseSummaryJob(value: unknown): SummaryEmailJob {
  const job = value as SummaryEmailJob;
  if (!job || !/^[a-z0-9]{15}$/.test(job.job_id) ||
      !["queued", "preparing", "sending", "accepted", "failed", "delivery_uncertain"].includes(job.state) ||
      typeof job.code !== "string" || job.code.length > 64 || typeof job.created !== "string" ||
      !Number.isSafeInteger(job.recording_count) || job.recording_count < 1 || job.recording_count > 10 ||
      job.status_url !== `/mobile/v1/recording-summary-emails/${job.job_id}`) throw new TypeError("Invalid summary status");
  return job;
}
export async function previewSummaryEmail(ids: string[], signal?: AbortSignal, recipient?: string): Promise<SummaryEmailPreview> {
  const selected = canonicalIds(ids);
  const value = await parseResponse<SummaryEmailPreview>(await authorizedFetch(`${route}/preview`, {
    method: "POST", body: JSON.stringify({recording_ids: selected, ...(recipient === undefined ? {} : {recipient})}), signal,
  }));
  if (!value || typeof value.recipient !== "string" || value.recipient.length > 254 ||
      typeof value.can_override_recipient !== "boolean" ||
      !/^[0-9a-f]{64}$/.test(value.preview_revision) || typeof value.sendable !== "boolean" ||
      !Array.isArray(value.recordings) || value.recordings.length !== selected.length ||
      value.recordings.some((r, i) => r.recording_id !== selected[i] || typeof r.label !== "string" ||
        typeof r.date !== "string" || !(r.duration_seconds === null || Number.isFinite(r.duration_seconds)) ||
        typeof r.metrics_available !== "boolean" || typeof r.stages_available !== "boolean" ||
        typeof r.source_mismatch !== "boolean" || !Array.isArray(r.notices) || r.notices.some(n => typeof n !== "string"))) {
    throw new TypeError("Invalid summary review");
  }
  return value;
}
export async function createSummaryEmail(request: SummaryEmailRequest): Promise<SummaryEmailJob> {
  const body = {...request, recording_ids: canonicalIds(request.recording_ids)};
  return parseSummaryJob(await parseResponse(await authorizedFetch(route, {method: "POST", body: JSON.stringify(body)})));
}
export async function getSummaryEmail(id: string, signal?: AbortSignal): Promise<SummaryEmailJob> {
  if (!/^[a-z0-9]{15}$/.test(id)) throw new TypeError("Invalid summary identity");
  const job = parseSummaryJob(await parseResponse(await authorizedFetch(`${route}/${id}`, {signal})));
  if (job.job_id !== id) throw new TypeError("Summary identity changed");
  return job;
}
export async function listSummaryEmails(cursor: string | null = null, signal?: AbortSignal): Promise<{items: SummaryEmailJob[]; next_cursor: string | null}> {
  const params = new URLSearchParams({limit: "10"});
  if (cursor) params.set("cursor", cursor);
  const value = await parseResponse<{items: unknown[]; next_cursor: string | null}>(await authorizedFetch(`${route}?${params}`, {signal}));
  if (!value || !Array.isArray(value.items) || value.items.length > 10 ||
      !(value.next_cursor === null || typeof value.next_cursor === "string" && value.next_cursor.length <= 256)) throw new TypeError("Invalid summary history");
  return {items: value.items.map(parseSummaryJob), next_cursor: value.next_cursor};
}
